<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BButton from '@/components/common/bButton.vue'
import BModal from '@/components/common/BModal.vue'
import SignatureDocumentFrame from '@/components/resource/signatureCampaign/SignatureDocumentFrame.vue'
import { useApplicationStore } from '@/config/store/applicationStore'
import { isCampaignDocumentSigned, useMySignatureCampaigns } from '@/composables/useMySignatureCampaigns'
import { useYouSignSignature } from '@/composables/useYouSignSignature'
import type { TMySignatureCampaignDocument } from '@/services/signatureCampaign/interface'

const props = defineProps<{
  /** Pause while another blocking modal (password change) is on screen. */
  paused?: boolean
}>()

const { t } = useI18n()
const route = useRoute()
const applicationStore = useApplicationStore()
const { pendingMandatory, load, documents } = useMySignatureCampaigns()

const activeId = ref<string | null>(null)
const openFailed = ref(false)
let opening = false

const activeDocument = computed(() =>
  documents.value.find(document => document._id === activeId.value) || null,
)

const queuePosition = computed(() => {
  if (!activeDocument.value) return 0
  const inQueue = pendingMandatory.value.findIndex(document => document._id === activeDocument.value?._id)
  return inQueue >= 0 ? inQueue + 1 : pendingMandatory.value.length
})

const refresh = async () => {
  await load()
  const current = documents.value.find(document => document._id === activeId.value)
  return current ? isCampaignDocumentSigned(current) : true
}

const openDocument = async (document: TMySignatureCampaignDocument) => {
  if (opening) return
  opening = true
  openFailed.value = false
  activeId.value = document._id
  try {
    await openModal()
  } finally {
    opening = false
  }
}

const openNext = async () => {
  await load()
  const next = pendingMandatory.value[0]
  if (!next) {
    closeModal()
    activeId.value = null
    return
  }
  await openDocument(next)
}

const {
  isOpen,
  isOpening,
  linkURL,
  openModal,
  closeModal,
} = useYouSignSignature({
  signatureDocument: () => activeDocument.value?.signatureDocument,
  refresh,
  keepOpenOnError: true,
  onSigned: () => { void openNext() },
  onError: () => {
    openFailed.value = true
    applicationStore.pushError({ title: t('openingError'), color: 'error' })
  },
})

const tryOpenPending = async () => {
  if (props.paused || isOpen.value || opening) return
  await load()
  const next = pendingMandatory.value[0]
  if (next) await openDocument(next)
}

watch(
  () => [props.paused, route.fullPath] as const,
  () => { void tryOpenPending() },
  { immediate: true },
)
</script>

<template>
  <BModal
    :model-value="isOpen"
    class="w-fit"
    data-cy="signature-mandatory-dialog"
    :disable-back-drop="true"
    :disable-close="true"
    @update:model-value="closeModal"
  >
    <template #header>
      <div class="flex min-w-0 flex-col pr-4">
        <h2 class="font-semibold">
          {{ t('title') }}
        </h2>
        <p v-if="activeDocument" class="subtitle text-sm truncate">
          {{ activeDocument.campaign.display }}
          <span v-if="pendingMandatory.length > 1">
            · {{ t('progress', { current: queuePosition, total: pendingMandatory.length }) }}
          </span>
        </p>
      </div>
    </template>

    <SignatureDocumentFrame
      v-if="!openFailed"
      :is-opening="isOpening"
      :link-url="linkURL"
      :title="t('title')"
      :opening-label="t('openingDocument')"
    />
    <div v-else class="flex flex-col items-center justify-center gap-3 px-8 py-12">
      <p class="text-sm text-center">
        {{ t('openingError') }}
      </p>
      <BButton v-if="activeDocument" size="dense" data-cy="signature-mandatory-retry" @click="openDocument(activeDocument)">
        {{ t('retry') }}
      </BButton>
    </div>
  </BModal>
</template>

<i18n>
{
  "fr": {
    "title": "Document obligatoire à signer",
    "progress": "{current} / {total}",
    "openingDocument": "Ouverture du document...",
    "openingError": "Impossible d'ouvrir le document à signer.",
    "retry": "Réessayer"
  },
  "en": {
    "title": "Mandatory document to sign",
    "progress": "{current} / {total}",
    "openingDocument": "Opening document...",
    "openingError": "Unable to open the document to sign.",
    "retry": "Try again"
  }
}
</i18n>
